import type {PanelLayout, PanelWithConfig} from '../../../util/panelTypes';
import {SmoothingTypeValues} from '../../elements/SmoothingConfig';

// All fields in the type should exist, but can be undefined
export type Complete<T> = {
  [P in keyof Required<T>]: Pick<T, P> extends Required<Pick<T, P>>
    ? T[P]
    : T[P] | undefined;
};

/**
 * PanelSettings are settings that apply to a _panel_; not necessarily any
 * one specific panel, but panels in general. They may be used to define
 * common settings for a group of panels, such as all panels in a workspace
 * or section of a workspace.
 */
export interface Settings {
  /** Whether run names should be colored in panel tooltips */
  colorRunNames?: boolean;
  /** "no crop" option for chart legends on the primary chart to accommodate long run names with crucial information in the middle */
  displayFullRunName?: boolean;
  excludeOutliers?: ExcludeOutliersValues;
  localSmoothingSettingsActive?: number;
  localxAxisSettingsActive?: number;
  /** Workspace setting level uses `maxRuns`, but panel config uses `limit`. This setting tells us maximum number of runs to include for a panel */
  maxRuns?: number;
  pointVisualizationMethod?: PointVisualizationOptions; // Default point vizualization method for runs line plots
  smoothingActive?: boolean;
  smoothingType?: SmoothingTypeValues;
  smoothingWeight?: number;
  /** workspace setting level uses `supressLegends` but panel configs use `showLegend`. We want resolve to the panel config when we feed in setting information. This setting tells us whether legends should be hidden in runs line plots */
  suppressLegends?: boolean;
  tooltipNumberOfRuns?: TooltipNumberOfRunsOptions; // Determines how many runs are displayed in panel tooltips
  useRunsTableGroupingInPanels?: boolean;
  xAxis?: XAxisValues | string;
  xAxisActive?: boolean;
  xAxisMax?: number;
  xAxisMin?: number;
}

export interface SmoothingSettings {
  smoothingWeight: number;
  smoothingType: SmoothingTypeValues;
}

export enum TooltipNumberOfRunsOptions {
  Single = 'single',
  Default = 'default',
  All = 'all_runs',
}

export enum OrganizationPrefix {
  FirstPrefix = 1,
  LastPrefix,
}

/**
 * Indicates which menu was selected in the Settings UX
 */
export type SelectedSettingType = 'line-plot' | 'workspace-layout';

export type YAxisType = 'linear' | 'log';
export type XAxisType = 'linear' | 'log' | 'time';

export enum XAxisValues {
  Step = '_step',
  RunTime = '_runtime',
  Timestamp = '_timestamp',
  AbsoluteRunTime = '_absolute_runtime',
}

export type XAxisSettingType = {
  xLogScale?: boolean;
  xAxis?: XAxisValues | string;
  xAxisMin?: number;
  xAxisMax?: number;
};

export type YAxisSettingType = {
  yLogScale?: boolean;
  yAxisMin?: number;
  yAxisMax?: number;
};

/**
 * These are either the workspace or section settings.
 * They need to be further resolved at the individual panel level.
 *
 * We are slowly migrating settings to a more consolidated structure.
 * This type helps with this process.
 */
export type LinePlotSettings = XAxisSettingType &
  YAxisSettingType & {
    excludeOutliers?: ExcludeOutliersValues;

    // smoothing
    smoothingWeight?: number;
    smoothingType?: SmoothingTypeValues;

    /**
     * workspace setting level uses `supressLegends` but panel configs use `showLegend`.
     * We want resolve to the panel config when we feed in setting information.
     * This setting tells us whether legends should be hidden in runs line plots
     */
    showLegend?: boolean;
    suppressLegends?: boolean;

    /**
     * Workspace setting level uses `maxRuns`, but panel config uses `limit`.
     * This setting tells us maximum number of runs to include for a panel
     */
    maxRuns?: number;
    limit?: number;

    /** respect the runs table grouping in the chart */
    useRunsTableGroupingInPanels?: boolean;
    /** Whether run names should be colored in panel tooltips */
    colorRunNames?: boolean;
    /** if true, only show the highlighted run in companion plots */
    highlightedCompanionRunOnly?: boolean;

    /** Default point vizualization method for runs line plots */
    pointVisualizationMethod?: PointVisualizationOptions;
    /** Determines how many runs are displayed in panel tooltips */
    tooltipNumberOfRuns?: TooltipNumberOfRunsOptions;
    /** "no crop" option for chart legends on the primary chart to accommodate long run names with crucial information in the middle */
    displayFullRunName?: boolean;
  };

export type DeprecatedLinePlotSettings = {
  ignoreOutliers?: boolean;
  showMinMaxOnHover?: boolean;
};

export type WorkspaceLayoutSettings = {
  autoOrganizePrefix?: OrganizationPrefix;
  /** if false, we'll hide sections with zero panels */
  showEmptySections?: boolean;
  /**
   * whether panels will be auto-generated from logged keys (for backwards
   * compatibility, undefined defaults to true). "pending" means user will
   * be explicitly prompted to select auto or manual mode.
   */
  shouldAutoGeneratePanels?: boolean | 'pending';
  /** sort panels alphabetically */
  sortAlphabetically?: boolean;
};

export type SettingSelectOptionType = {
  value: string;
  label: string;
};

export type LinePlotPanel = PanelWithConfig<'Run History Line Plot'> & {
  key?: string | undefined;
} & PanelLayout;

// point aggregation method
export const POINT_VISUALIZATION_OPTIONS = {
  BucketingGorilla: 'bucketing-gorilla',
  ForceBucketing: 'force-bucketing',
  Sampling: 'sampling',
  SamplingByDefault: 'sampling-by-default',
} as const;
export type PointVisualizationOptions =
  (typeof POINT_VISUALIZATION_OPTIONS)[keyof typeof POINT_VISUALIZATION_OPTIONS];

export type DerivedPointVisualizationOption = 'bucketing-gorilla' | 'sampling'; // narrow down option to be used in radio UX

export const isBucketingOption = (option: DerivedPointVisualizationOption) => {
  return option === POINT_VISUALIZATION_OPTIONS.BucketingGorilla;
};
export const isFullFidelityMode = isBucketingOption;

export type ExcludeOutliersValues = 'include-outliers' | 'exclude-outliers';
